/**
 * The Ocean cargo section's values <-> the neutral ocean cargo block. The container
 * family dropdown (Dry van, Reefer, ...) is form-only: the request carries
 * the size/type code (20DV, 40HCRF, ...).
 */
import type {
  OceanCargoRequest,
  OceanItemRequest,
} from '../../../apis/lanePlanner';

export const MAX_ITEMS = 3;

/** Container families -> the size/type codes of the neutral request. */
export const CONTAINER_TYPES: Array<{
  value: string;
  label: string;
  sizes: Array<{ value: string; label: string }>;
}> = [
  {
    value: 'DRY',
    label: 'Dry van',
    sizes: [
      { value: '20DV', label: '20 DV' },
      { value: '40DV', label: '40 DV' },
      { value: '40HC', label: '40 HC' },
      { value: '45HC', label: '45 HC' },
    ],
  },
  {
    value: 'REEFER',
    label: 'Reefer',
    sizes: [
      { value: '20RF', label: '20 RF' },
      { value: '40HCRF', label: '40 HC RF' },
      { value: '45RF', label: '45 RF' },
    ],
  },
  {
    value: 'NOR',
    label: 'Non-operating reefer',
    sizes: [{ value: '40NOR', label: '40 NOR' }],
  },
  {
    value: 'OPEN_TOP',
    label: 'Open top',
    sizes: [
      { value: '20OT', label: '20 OT' },
      { value: '40OT', label: '40 OT' },
      { value: '40HCOT', label: '40 HC OT' },
    ],
  },
  {
    value: 'FLAT_RACK',
    label: 'Flat rack',
    sizes: [
      { value: '20FR', label: '20 FR' },
      { value: '40FR', label: '40 FR' },
      { value: '40HCFR', label: '40 HC FR' },
    ],
  },
  {
    value: 'TANK',
    label: 'Tank',
    sizes: [
      { value: '20TK', label: '20 TK' },
      { value: '40TK', label: '40 TK' },
    ],
  },
];

export interface OceanItemValues {
  quantity?: number | string;
  weight?: number | string;
  containerSizeType?: string;
  unitType?: 'PALLET' | 'BOX';
  length?: number | string;
  width?: number | string;
  height?: number | string;
  volume?: number | string;
  stackable?: boolean;
}

export interface OceanCargoValues {
  shipmentType: 'FCL' | 'LCL';
  containerType: string;
  /** LCL: per-unit dims vs total volume. */
  lclMode: 'PER_UNIT' | 'TOTAL';
  items: OceanItemValues[];
  hazardous?: boolean;
  /** Optional Zoomlogi quote reference (PO / internal id). */
  reference?: string;
}

export const defaultOceanCargoValues = (): OceanCargoValues => ({
  shipmentType: 'FCL',
  containerType: 'DRY',
  lclMode: 'PER_UNIT',
  items: [{ quantity: 1, stackable: true }],
});

/** Validated section values -> the `ocean` block of the search request. */
export const buildOceanCargo = (
  values: OceanCargoValues
): OceanCargoRequest => {
  const fcl = values.shipmentType === 'FCL';
  const totalsOnly = !fcl && values.lclMode === 'TOTAL';
  const items = (values.items || []).slice(0, MAX_ITEMS).map((item) => {
    const base: OceanItemRequest = {
      quantity: Number(item.quantity),
      weight_kg: Number(item.weight),
    };
    if (fcl) {
      base.container_size_type = item.containerSizeType;
    } else {
      base.unit_type = item.unitType || 'PALLET';
      if (totalsOnly) {
        base.volume_m3 = Number(item.volume);
      } else {
        base.length_cm = Number(item.length);
        base.width_cm = Number(item.width);
        base.height_cm = Number(item.height);
      }
      base.stackable = item.stackable !== false;
    }
    return base;
  });
  return {
    shipment_type: values.shipmentType,
    items,
    hazmat: !!values.hazardous,
    totals_only: totalsOnly,
  };
};

/** Reverse of `buildOceanCargo`: a stored `ocean` block -> section values. */
export const oceanCargoValuesFromRequest = (
  ocean: OceanCargoRequest
): OceanCargoValues => {
  const defaults = defaultOceanCargoValues();
  const items = ocean.items ?? [];
  const firstSize = items[0]?.container_size_type;
  const containerType =
    (firstSize &&
      CONTAINER_TYPES.find((type) =>
        type.sizes.some((size) => size.value === firstSize)
      )?.value) ||
    'DRY';
  return {
    ...defaults,
    shipmentType: ocean.shipment_type,
    containerType,
    lclMode: ocean.totals_only ? 'TOTAL' : 'PER_UNIT',
    items: items.length
      ? items.map((item) => ({
          quantity: item.quantity,
          weight: item.weight_kg,
          containerSizeType: item.container_size_type ?? undefined,
          unitType: item.unit_type ?? undefined,
          length: item.length_cm ?? undefined,
          width: item.width_cm ?? undefined,
          height: item.height_cm ?? undefined,
          volume: item.volume_m3 ?? undefined,
          stackable: item.stackable !== false,
        }))
      : defaults.items,
    hazardous: !!ocean.hazmat,
  };
};
